import React, { ReactNode } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import HBText, { TAllColorKeys } from '../HBText/HBText';
import { newTextStyles } from '../HBText/HBText.styles';
import { InfoCircle16 } from '@honeybook/icons';
import HBInfoIcon from '../HBInfoIcon/HBInfoIcon';
import HBClickable from '../HBClickable/HBClickable';
import { TSize, CSSDimensionValue } from '../../tokens/types';
import { vars } from '../../theme';

export interface HBBaseFormFieldProps {
    /** HTML `id` for the field; forwarded to the input and used as the `<label htmlFor>` target. */
    id?: string;
    /** Label text or element rendered above (or beside) the input control. */
    label?: ReactNode;
    /** Secondary text rendered below the label, styled in a muted color. */
    subLabel?: ReactNode;
    /** Tertiary descriptive text rendered below the subLabel with smaller typography. */
    caption?: ReactNode;
    /** Position of the label relative to the child input control. */
    placement?: 'top' | 'left' | 'right';
    /** Outer margin applied to the field container. Accepts any CSS dimension value. */
    margin?: CSSDimensionValue;
    /** When true, appends an asterisk to the label and sets the `required` attribute on the input. */
    required?: boolean;
    /** Overrides the label text size independently of the field size; defaults to the value of size. */
    labelSize?: TSize | 'xs';
    /** Controls text size for the label and message area. Defaults to `sm`. */
    size?: TSize | 'xs';
    /** Disables the input and applies a not-allowed cursor to the entire field. */
    disabled?: boolean;
    /** Additional CSS class applied to the field container. */
    className?: string;
    /** When true, applies error styling to the input. */
    error?: boolean;
    /** Error text displayed below the field when `error` is true. */
    errorMessage?: string;
    /** Supplementary guidance shown below the field; only visible when showHintMessage is true or the field has focus. */
    hintMessage?: string | ReactNode;
    /** Forces the hint message to always be visible rather than only on focus. */
    showHintMessage?: boolean;
    /** Message shown below the field when disabled, replacing any error or hint message. */
    disabledMessage?: string;
    /** Reserves space for the message area below the field; set to false to collapse it entirely. */
    withMessage?: boolean;
    /** Content rendered inside an info icon tooltip adjacent to the label. */
    tooltip?: ReactNode;
    /** Custom icon component for the label tooltip trigger; defaults to InfoCircle16. */
    tooltipIcon?: typeof InfoCircle16;
    /** Color of the tooltip icon in its default state. */
    tooltipIconColor?: string;
    /** Color of the tooltip icon on hover. */
    tooltipIconHoverColor?: string;
    /** Keeps the tooltip open when the user hovers over the tooltip content itself. */
    tooltipKeepOpenOnHover?: boolean;
    /** Opens the tooltip automatically on first render. */
    tooltipEnableInitialOpen?: boolean;
    /** Allows the tooltip to close when clicking outside of it. */
    tooltipShouldSupportClickOutside?: boolean;
    /** Callback fired when the tooltip opens. */
    onTooltipOpen?: () => void;
    /** Makes the entire label area clickable; when provided, wraps the label in an interactive element. */
    onLabelClick?: (event: React.SyntheticEvent<HTMLSpanElement>) => void;
    /** Width of the field container. Defaults to `100%`. */
    width?: string;
    /** Enables DS Next visual styling (updated disabled colors and alignment). Temporary — will be removed post-migration. @deprecated */
    isDSNext?: boolean;
}

export interface CounterProps {
    /** Displays a character counter (currLength/maxLength) next to the label; auto-populated by splitFieldProps when maxLength and a string value are present. */
    counterProps?: {
        currLength: number;
        maxLength: number;
    };
}

type Props = HBBaseFormFieldProps & CounterProps;

const HBBaseFormField: React.FC<Props> = props => {
    const {
        children,
        margin,
        placement,
        disabled,
        className,
        width,
        isDSNext
    } = props;

    return (
        <Container
            className={className}
            $disabled={disabled}
            $width={width}
            $margin={margin}
        >
            <ControlWrapper
                $isFlexColumn={placement === 'top'}
                $isDSNext={isDSNext}
            >
                {placement === 'right' ? (
                    <>
                        {children}
                        <Label {...props} />
                    </>
                ) : (
                    <>
                        <Label {...props} />
                        {children}
                    </>
                )}
            </ControlWrapper>
            <Message {...props} />
        </Container>
    );
};

HBBaseFormField.defaultProps = {
    placement: 'top',
    size: 'sm',
    showHintMessage: false,
    tooltipIcon: InfoCircle16,
    tooltipIconColor: vars.colors.text.tertiary,
    tooltipIconHoverColor: vars.colors.text.secondary,
    tooltipKeepOpenOnHover: false,
    width: '100%',
    withMessage: true
};

export default HBBaseFormField;

const Label = ({
    label,
    subLabel,
    caption,
    placement,
    required,
    size,
    labelSize = size,
    disabled,
    id,
    tooltip,
    tooltipIcon,
    tooltipIconColor,
    tooltipIconHoverColor,
    tooltipKeepOpenOnHover,
    tooltipEnableInitialOpen,
    tooltipShouldSupportClickOutside,
    onTooltipOpen,
    onLabelClick,
    counterProps,
    isDSNext
}: Props) => {
    if (!label && !counterProps) {
        return null;
    }
    const DynamicLabelContainer = onLabelClick
        ? ClickableLabelContainer
        : LabelContainer;

    return (
        <DynamicLabelContainer onClick={onLabelClick}>
            {label && (
                <LabelInnerContainer
                    htmlFor={id}
                    $size={labelSize}
                    $placement={placement}
                    $disabled={disabled}
                    $isDSNext={isDSNext}
                >
                    <MainLabelContainer>
                        <span>
                            {label}
                            {required && ' *'}
                        </span>
                        <HBInfoIcon
                            tooltip={tooltip}
                            icon={tooltipIcon}
                            iconColor={tooltipIconColor}
                            iconHoverColor={tooltipIconHoverColor}
                            keepOpenOnHover={tooltipKeepOpenOnHover}
                            tooltipEnableInitialOpen={tooltipEnableInitialOpen}
                            tooltipShouldSupportClickOutside={
                                tooltipShouldSupportClickOutside
                            }
                            onTooltipOpen={onTooltipOpen}
                        />
                    </MainLabelContainer>
                    {subLabel && <SubLabel>{subLabel}</SubLabel>}
                    {caption && <Caption $size={labelSize}>{caption}</Caption>}
                </LabelInnerContainer>
            )}
            {counterProps && (
                <Counter
                    $isOverMax={
                        counterProps.currLength > counterProps.maxLength
                    }
                    $size={size}
                >
                    {counterProps.currLength}/{counterProps.maxLength}
                </Counter>
            )}
        </DynamicLabelContainer>
    );
};

const Message = ({
    disabled,
    error,
    errorMessage,
    hintMessage,
    disabledMessage,
    showHintMessage,
    withMessage,
    isDSNext
}: Props) => {
    const message = disabled
        ? disabledMessage
        : error
        ? errorMessage
        : hintMessage;

    if (!withMessage || !message) {
        return null;
    }

    const isVisible =
        (error && !!errorMessage) ||
        (disabled && !!disabledMessage) ||
        (showHintMessage && !!hintMessage);

    const getMessageColor = (): TAllColorKeys => {
        if (disabled) {
            return isDSNext ? 'text.disabled' : 'text.tertiary';
        }
        if (error) {
            return 'text.danger';
        }
        return 'text.secondary';
    };
    const color = getMessageColor();

    return (
        <MessageContainer type="body12" $isVisible={isVisible} color={color}>
            {message}
        </MessageContainer>
    );
};

const SHARED_DUMMY_PROPS: Pick<
    HBBaseFormFieldProps,
    'id' | 'size' | 'error' | 'disabled' | 'width'
> = {
    id: undefined,
    size: undefined,
    error: undefined,
    disabled: undefined,
    width: undefined
};

const FIELD_DUMMY_PROPS: Required<
    Omit<HBBaseFormFieldProps, keyof typeof SHARED_DUMMY_PROPS>
> = {
    className: undefined,
    errorMessage: undefined,
    hintMessage: undefined,
    disabledMessage: undefined,
    withMessage: undefined,
    label: undefined,
    subLabel: undefined,
    caption: undefined,
    labelSize: undefined,
    placement: undefined,
    required: undefined,
    tooltip: undefined,
    tooltipIcon: undefined,
    tooltipIconColor: undefined,
    tooltipIconHoverColor: undefined,
    tooltipKeepOpenOnHover: undefined,
    tooltipEnableInitialOpen: undefined,
    tooltipShouldSupportClickOutside: undefined,
    onTooltipOpen: undefined,
    onLabelClick: undefined,
    showHintMessage: undefined,
    margin: undefined,
    isDSNext: false
};

export const splitFieldProps = <T extends HBBaseFormFieldProps>(props: T) =>
    Object.keys(props).reduce(
        (acc, key) => {
            if (key in SHARED_DUMMY_PROPS) {
                acc.fieldProps[key] = props[key];
                acc.inputProps[key] = props[key];
            } else if (key in FIELD_DUMMY_PROPS) {
                acc.fieldProps[key] = props[key];
            } else if (
                key === 'maxLength' &&
                'value' in props &&
                typeof props.value === 'string'
            ) {
                acc.fieldProps.counterProps = {
                    currLength: props.value.length,
                    maxLength: props[key]
                };
            } else {
                acc.inputProps[key] = props[key];
            }
            return acc;
        },
        {
            fieldProps: {} as Props,
            inputProps: {} as Omit<T, keyof typeof FIELD_DUMMY_PROPS>
        }
    );

// -------------------------- Styles -------------------------- //

const Container = styled.div<{
    $disabled: boolean;
    $width: string;
    $margin: string;
}>`
    width: ${({ $width }) => $width};
    display: flex;
    flex-direction: column;
    position: relative;
    ${({ $margin, $disabled }) => css`
        ${$margin &&
        css`
            margin: ${$margin};
        `}
        ${$disabled &&
        css`
            &,
            * {
                cursor: not-allowed !important;
            }
        `}
    `}
`;

const ControlWrapper = styled.div<{
    $isFlexColumn: boolean;
    $isDSNext: boolean;
}>`
    display: flex;
    ${({ $isFlexColumn, $isDSNext }) =>
        $isFlexColumn
            ? css`
                  flex-direction: column;
              `
            : css`
                  align-items: ${$isDSNext ? 'flex-start' : 'center'};
              `}
`;

const labelContainerStyles = css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
`;

const LabelContainer = styled.div`
    ${labelContainerStyles}
`;

const ClickableLabelContainer = styled(HBClickable)`
    ${labelContainerStyles}
`;

const LabelInnerContainer = styled.label<{
    $size: HBBaseFormFieldProps['labelSize'];
    $placement: HBBaseFormFieldProps['placement'];
    $disabled: HBBaseFormFieldProps['disabled'];
    $isDSNext: HBBaseFormFieldProps['isDSNext'];
}>`
    display: flex;
    flex-direction: column;
    color: ${vars.colors.text.primary};

    i {
        display: flex;
    }

    ${({ $placement, $size, htmlFor, $disabled, $isDSNext }) => css`
        cursor: ${htmlFor ? 'pointer' : 'default'};
        ${PLACEMENT_STYLES[$placement]}
        ${LABEL_STYLES[$size]}
        ${$disabled &&
        css`
            &,
            * {
                color: ${$isDSNext
                    ? vars.colors.text.disabled
                    : vars.colors.text.tertiary} !important;
            }
        `}
    `}
`;

const SubLabel = styled.span`
    color: ${vars.colors.text.secondary};
`;

const MainLabelContainer = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    // white-space: nowrap; // TODO: Uncomment carefully
`;

const Caption = styled.span<{
    $size?: HBBaseFormFieldProps['labelSize'];
}>`
    color: ${vars.colors.text.secondary};
    ${({ $size }) => CAPTION_STYLES[$size]}
`;

const LABEL_STYLES: Record<HBBaseFormFieldProps['labelSize'], any> = {
    xs: newTextStyles.body12,
    sm: newTextStyles.body14,
    md: newTextStyles.body14,
    lg: newTextStyles.body16,
    xl: newTextStyles.body18
};

const CAPTION_STYLES: Record<HBBaseFormFieldProps['labelSize'], any> = {
    xs: newTextStyles.body10,
    sm: newTextStyles.body12,
    md: newTextStyles.body12,
    lg: newTextStyles.body14,
    xl: newTextStyles.body16
};

const PLACEMENT_STYLES: Record<
    HBBaseFormFieldProps['placement'],
    FlattenSimpleInterpolation
> = {
    top: css`
        padding: 0 0 6px 0;
    `,
    right: css`
        padding: 0 0 0 8px;
    `,
    left: css`
        &:only-child,
        ~ *:last-child {
            padding: 0 8px 0 0;
        }
    `
};

const MessageContainer = styled(HBText)<{ $isVisible: boolean }>`
    margin-top: 4px;
    display: none;
    ${Container}:focus-within & {
        display: inline;
    }
    ${({ $isVisible }) =>
        $isVisible &&
        css`
            display: inline;
        `}
`;

const Counter = styled.span<{
    $isOverMax: boolean;
    $size?: HBBaseFormFieldProps['size'];
}>`
    color: ${vars.colors.text.secondary};
    ${({ $size }) => $size && LABEL_STYLES[$size]}
    ${({ $isOverMax }) =>
        $isOverMax &&
        css`
            color: ${vars.colors.text.danger};
        `}
`;
